// Pain points & solution content for the "Найдите своё решение" flow
// Currently only one pain has a full solution page; others show a friendly placeholder.

export type PainItem = {
  id: string
  text: string
}

export type PainCategory = {
  id: string
  icon: string
  title: string
  headline: string
  subheadline: string
  highlighted?: boolean
  pains: PainItem[]
}

export type Role = {
  id: string
  icon: string
  label: string
  available: boolean
}

export type Step = {
  title?: string
  description: string
  image?: string
  images?: string[]
  isMobile?: boolean
}

export type PainSolution = {
  // metadata for breadcrumb / back nav
  categoryId: string
  categoryIcon: string
  categoryTitle: string
  painId: string
  // solution page content
  headline: string
  subheadline: string
  whatYouSee: {
    image?: string
    images?: string[]
    video?: string
    description: string
    features?: string[]
    isMobile?: boolean
  }
  howItWorksVideo?: string
  steps: Step[]
  outcomes: string[]
}

export const ROLES: Role[] = [
  { id: 'owner', icon: '👔', label: 'Владелец / Заведующая', available: true },
  { id: 'methodist', icon: '📋', label: 'Методист', available: false },
  { id: 'teacher', icon: '🧑‍🏫', label: 'Воспитатель', available: false },
  { id: 'nurse', icon: '🩺', label: 'Медсестра', available: false },
]

export const CATEGORIES: PainCategory[] = [
  {
    id: 'attendance',
    icon: '📊',
    title: 'Посещаемость и учёт детей',
    headline: 'Видеть посещаемость без чатов и Excel',
    subheadline: 'Что именно вас беспокоит?',
    pains: [
      {
        id: 'attendance-whatsapp',
        text: 'Каждое утро спрашиваю в WhatsApp, кто пришёл, веду табель в Excel или на бумаге',
      },
      {
        id: 'attendance-churn',
        text: 'Не знаю, сколько детей ушло из садика и почему',
      },
      {
        id: 'attendance-trend',
        text: 'Не вижу, кто из детей стал реже ходить',
      },
    ],
  },
  {
    id: 'parents',
    icon: '💬',
    title: 'Общение с родителями',
    headline: 'Связь с родителями без хаоса в WhatsApp',
    subheadline: 'Что именно вас беспокоит?',
    pains: [
      {
        id: 'parents-birthdays',
        text: 'Пропускаю дни рождения детей, не успеваю подготовиться',
      },
      {
        id: 'parents-chats',
        text: 'Не вижу, что происходит в WhatsApp-чатах с родителями',
      },
      {
        id: 'parents-info',
        text: 'Не уверена, что родители получают всю информацию о детях',
      },
    ],
  },
  {
    id: 'staff',
    icon: '👥',
    title: 'Работа сотрудников',
    headline: 'Сотрудники без потерянных задач',
    subheadline: 'Что именно вас беспокоит?',
    pains: [
      {
        id: 'staff-attendance',
        text: 'Спрашиваю в WhatsApp, кто пришёл на работу и во сколько',
      },
      {
        id: 'staff-tasks',
        text: 'Задачи теряются в чатах, не понимаю, что сделано, а что нет',
      },
    ],
  },
  {
    id: 'documents',
    icon: '📁',
    title: 'Документы и подготовка к проверкам',
    headline: 'Спокойно встречать любую проверку',
    subheadline: 'Что именно вас беспокоит?',
    highlighted: true,
    pains: [
      {
        id: 'docs-nurse-journals',
        text: 'Не знаю, ведёт ли медсестра все обязательные журналы',
      },
      {
        id: 'docs-methodology',
        text: 'Не уверена, что у нас есть все методические документы',
      },
      {
        id: 'docs-deadlines',
        text: 'Не отслеживаю сроки документов — что-то может быть просрочено',
      },
      {
        id: 'docs-storage',
        text: 'Документы разбросаны, нет единого хранилища',
      },
    ],
  },
  {
    id: 'finance',
    icon: '💰',
    title: 'Финансы и оплаты родителей',
    headline: 'Видеть финансы садика, а не догадываться',
    subheadline: 'Что именно вас беспокоит?',
    pains: [
      {
        id: 'finance-debtors',
        text: 'Не вижу, кто из родителей ещё должен',
      },
      {
        id: 'finance-slow-payments',
        text: 'Оплата от родителей собирается медленно',
      },
      {
        id: 'finance-overpay',
        text: 'Закупаем без калькуляции на глаз - каждый месяц теряем деньги',
      },
      {
        id: 'finance-child-view',
        text: 'По каждому ребёнку нет единой картины: оплата, посещаемость, документы',
      },
    ],
  },
  {
    id: 'nutrition',
    icon: '🍽',
    title: 'Питание и закупки',
    headline: 'Питание под контролем — от меню до склада',
    subheadline: 'Что именно вас беспокоит?',
    pains: [
      { id: 'nutrition-menu', text: 'Нет документации по перспективному меню' },
      { id: 'nutrition-tech-cards', text: 'Нет технологических карт' },
      { id: 'nutrition-calc', text: 'Нет калькуляции питания' },
      {
        id: 'nutrition-stock',
        text: 'Не вижу остатков на складе и не понимаю, что нужно докупить',
      },
    ],
  },
]

// Full solution pages — keyed by pain id
export const SOLUTIONS: Record<string, PainSolution> = {
  'attendance-churn': {
    categoryId: 'attendance',
    categoryIcon: '📊',
    categoryTitle: 'Посещаемость и учёт детей',
    painId: 'attendance-churn',
    headline: 'Дети уходят — а вы не знаете почему',
    subheadline:
      'Большинство садиков не ведут аналитику по выбывшим детям. А это значит: причины ухода повторяются из года в год, и удержать клиентов невозможно — потому потому что вы не видите, что их теряет.',
    whatYouSee: {
      images: [
        'https://cdn.magicpatterns.com/uploads/xvCiYkPocfiCKB1tQbBn7m/список.png',
        'https://cdn.magicpatterns.com/uploads/96ExRDdJELaH6MJmTGfyQS/история_оплаты.png',
        'https://cdn.magicpatterns.com/uploads/h3KvoRDRRXVRLVAFr86CV5/документы.png',
        'https://cdn.magicpatterns.com/uploads/u77vFnQs7PJhDUB7tPz1U6/Screenshot_2026-05-19_at_13.39.08.png',
      ],
      description:
        'Полная картина по каждому ушедшему ребёнку — и причины ухода всего садика на одном экране',
      features: [
        'Список всех выбывших детей с датой и причиной ухода',
        'История посещаемости — видно, когда ребёнок начал реже ходить',
        'История оплат и документов',
        'Аналитика причин ухода по садику: адаптация, переезд, цена, конфликт и т.д.',
      ],
    },
    steps: [
      {
        title: 'Шаг 1. Фиксация ухода',
        description:
          'Когда ребёнок выбывает, администратор отмечает причину из списка (или вписывает свою). Это занимает 30 секунд.',
      },
      {
        title: 'Шаг 2. Автоматический анализ',
        description:
          'Система собирает данные по всем выбывшим: причины, сроки, возраст ребёнка, группа. Вы видите паттерны, а не отдельные случаи.',
      },
      {
        title: 'Шаг 3. Ранние сигналы',
        description:
          'По посещаемости видно, кто из текущих детей начал реже ходить — это часто первый признак, что ребёнок уйдёт. У вас есть время поговорить с родителями раньше.',
      },
    ],
    outcomes: [
      'Видите реальные причины оттока — не догадки, а данные',
      'Замечаете "тревожных" детей до того, как они ушли',
      'Можете устранять системные причины (если 5 детей ушли из-за адаптации — пора менять подход)',
      'Удерживаете клиентов и сохраняете выручку',
    ],
  },
  'attendance-trend': {
    categoryId: 'attendance',
    categoryIcon: '📊',
    categoryTitle: 'Посещаемость и учёт детей',
    painId: 'attendance-trend',
    headline: 'Не видите, кто из детей стал реже ходить?',
    subheadline:
      'Ребёнок начинает пропускать дни — сначала по одному, потом чаще. К тому моменту, когда вы это замечаете, родители уже думают об уходе. А вы не можете повлиять, потому что не видите этого вовремя.',
    whatYouSee: {
      image:
        'https://cdn.magicpatterns.com/uploads/oAsUeM2cbcEurCCMmWtUyk/1.png',
      description:
        'Список детей, чья посещаемость падает — с конкретными цифрами',
      features: [
        'Дети, у которых посещаемость снизилась за последние 2-4 недели',
        'На сколько процентов снизилась — относительно их обычной нормы',
        'История пропусков: когда начал пропускать, сколько дней подряд',
        'Контакты родителей — прямо из карточки ребёнка',
      ],
    },
    steps: [
      {
        title: 'Список детей с посещаемостью на одном экране',
        description:
          'Вы видите всех активных детей с их % посещаемости. Сортируете по посещаемости и сразу замечаете тех, у кого она низкая.',
        image:
          'https://cdn.magicpatterns.com/uploads/oAsUeM2cbcEurCCMmWtUyK/1.png',
      },
      {
        title: 'Карточка ребёнка — вся история',
        description:
          'Кликаете на ребёнка — открывается карточка с историей посещений, историей оплат и контактами родителей.',
        images: [
          'https://cdn.magicpatterns.com/uploads/hXR1KPCTBUYdfwN4PaoeY1/Screenshot_2026-05-19_at_13.30.54.png',
          'https://cdn.magicpatterns.com/uploads/9EZhrmdhbbHT8V4thFXJxK/история_оплаты.png',
          'https://cdn.magicpatterns.com/uploads/jmb4RHABe3vER18bJV3Yxu/документы.png',
        ],
      },
      {
        title: 'Звонок родителю — прямо из карточки',
        description:
          'Контакты родителей перед глазами. Звоните, выясняете причины, удерживаете ребёнка — до того, как родители приняли решение об уходе.',
      },
    ],
    outcomes: [
      'Если у ребёнка посещаемость 40% при общей норме 80% — повод позвонить родителям сейчас, а не ждать заявления об уходе',
      'Если в одной группе несколько детей с низкой посещаемостью — пора посмотреть, что происходит в этой группе',
      'Звоните родителю раньше, чем он сам пришёл с разговором об уходе',
      'Удерживаете клиентов в 2-3 раза дешевле, чем привлекаете новых',
    ],
  },
  'attendance-whatsapp': {
    categoryId: 'attendance',
    categoryIcon: '📊',
    categoryTitle: 'Посещаемость и учёт детей',
    painId: 'attendance-whatsapp',
    headline:
      'Каждое утро спрашиваю в WhatsApp, кто пришёл, веду табель в Excel или на бумаге',
    subheadline: '',
    whatYouSee: {
      video:
        'https://cdn.magicpatterns.com/uploads/6rok2TssozhZMSznRcHJaN/2026-05-19 13.16.59.mp4',
      description: 'Полная картина с аналитикой для вас',
      isMobile: true,
    },
    steps: [
      {
        description:
          'Воспитатели отмечают посещение в два клика через приложение на телефоне.',
        images: [
          'https://cdn.magicpatterns.com/uploads/6sieQBNv4hptoPe6m7oBF3/2026-05-19_13.19.57.jpg',
          'https://cdn.magicpatterns.com/uploads/okZ49zBwqW9nauBMux5KHa/photo_2026-05-19_13.20.04.jpg',
        ],
        isMobile: true,
      },
    ],
    outcomes: [
      'Видите посещаемость по всем группам в одном экране',
      'Воспитатели отмечают детей сами — без ваших вопросов в чате',
      'История посещаемости по каждому ребёнку — за любой период',
      'Точные данные для расчёта оплаты и питания',
    ],
  },
  'parents-info': {
    categoryId: 'parents',
    categoryIcon: '💬',
    categoryTitle: 'Общение с родителями',
    painId: 'parents-info',
    headline: 'Не уверены, что родители получают всю информацию о детях?',
    subheadline:
      'Воспитатели ведут WhatsApp-чаты с родителями — но вы туда не входите. Что они отправляют? Получают ли все родители одинаковую информацию? Видят ли они, чем занимался ребёнок, что ел, как прошёл день? Вы не знаете.',
    whatYouSee: {
      isMobile: true,
      images: [
        'https://cdn.magicpatterns.com/uploads/e2JiFnrq93CgRsX2sAycPS/photo_2026-05-19_15.27.55.jpg',
        'https://cdn.magicpatterns.com/uploads/ry1FH8kPi6DDyjrgZFVqeu/photo_2026-05-19_15.27.58.jpg',
        'https://cdn.magicpatterns.com/uploads/7hQWMerLhhnzGhNJRHjRf9/photo_2026-05-19_15.27.52.jpg',
      ],
      description:
        'Отдельное приложение для родителей — вся информация о ребёнке в одном месте',
      features: [
        'Чем ребёнок занимался в течение дня',
        'Что ел и сколько съел',
        'Расписание и занятия',
        'Документы по ребёнку',
        'Доступ к камерам группы (если подключены)',
      ],
    },
    steps: [
      {
        title: 'Воспитатель вносит данные в Meelo',
        description:
          'В течение дня воспитатель фиксирует посещаемость, питание, активности — всё в одной системе.',
        image:
          'https://cdn.magicpatterns.com/uploads/pD6ufpzkx1b837jTBmjxfm/Screenshot_2026-05-19_at_15.27.25.png',
      },
      {
        title: 'Родитель видит это в приложении',
        description:
          'Информация автоматически появляется в приложении родителя. Никаких рассылок в WhatsApp, никаких «забыла отправить».',
        images: [
          'https://cdn.magicpatterns.com/uploads/e2JiFnrq93CgRsX2sAycPS/photo_2026-05-19_15.27.55.jpg',
          'https://cdn.magicpatterns.com/uploads/ry1FH8kPi6DDyjrgZFVqeu/photo_2026-05-19_15.27.58.jpg',
        ],
        isMobile: true,
      },
      {
        title: 'Вы видите, что видят родители',
        description:
          'Как владелец вы видите, какая информация уходит родителям, какие группы ведут учёт регулярно, а какие — нет.',
        image:
          'https://cdn.magicpatterns.com/uploads/qtG14nD5EkbQQiyQHNhP2G/Screenshot_2026-05-19_at_15.27.07.png',
      },
    ],
    outcomes: [
      'Если воспитатель одной группы ведёт всё в Meelo, а другой пропускает — видно сразу, и можно поговорить',
      'Если родители жалуются «нам ничего не присылают» — вы можете проверить и показать, что именно отправлялось',
      'Если родитель спрашивает «что ел сегодня ребёнок» — вы не отвечаете, родители смотрят сами в приложении',
      'Прозрачность для родителей = меньше тревожных звонков и больше доверия к садику',
    ],
  },
  'parents-chats': {
    categoryId: 'parents',
    categoryIcon: '💬',
    categoryTitle: 'Общение с родителями',
    painId: 'parents-chats',
    headline: 'Не видите, что происходит в чатах с родителями?',
    subheadline:
      'Воспитатели общаются с родителями в WhatsApp — вы туда не входите. Жалобы, просьбы, конфликты решаются без вашего ведома. Если родитель в итоге уйдёт — вы не узнаете, что начиналось всё с одного сообщения, на которое не ответили.',
    whatYouSee: {
      image:
        'https://cdn.magicpatterns.com/uploads/mi1tNxiXRCxtgfot9Wqita/Screenshot_2026-05-19_at_15.57.55.png',
      description:
        'Все обращения родителей — в одной системе. Никаких WhatsApp.',
      features: [
        'Родители оставляют обращения через приложение',
        'Каждое обращение — отдельная тема: жалоба, предложение, вопрос',
        'История переписки сотрудника с родителем — полностью',
        'Прикреплённые документы и фото',
        'Статус: решено / в работе / без ответа',
      ],
    },
    steps: [
      {
        title: 'Родитель создаёт обращение в приложении',
        description:
          'Вместо сообщения в WhatsApp родитель выбирает категорию (жалоба, вопрос, предложение) и пишет тему. Это структурирует общение с самого начала.',
      },
      {
        title: 'Сотрудник отвечает в системе',
        description:
          'Вся переписка хранится в одном месте. Никаких потерянных сообщений, никаких «я в личке писала, она не видела».',
        image:
          'https://cdn.magicpatterns.com/uploads/oh4wSibK3rTbmgh5k242Mk/Screenshot_2026-05-19_at_15.58.05.png',
      },
      {
        title: 'Вы видите все обращения по всему садику',
        description:
          'Открываете «Управление родителями» — видите каждое обращение, кто ответил, какой статус. Можете провалиться в любую переписку.',
        image:
          'https://cdn.magicpatterns.com/uploads/mi1tNxiXRCxtgfot9Wqita/Screenshot_2026-05-19_at_15.57.55.png',
      },
    ],
    outcomes: [
      'Если у одного воспитателя 5 жалоб за месяц, а у другого — 0, это уже разговор',
      'Если родитель пишет жалобу, а сотрудник не ответил 3 дня — вы видите это и вмешиваетесь до того, как родитель уйдёт',
      'Если возникает спорная ситуация, у вас есть полная история переписки, а не «я говорила» / «вы не говорили»',
      'Все коммуникации с родителями — официальный канал, а не личные WhatsApp воспитателей',
    ],
  },
  'staff-attendance': {
    categoryId: 'staff',
    categoryIcon: '👥',
    categoryTitle: 'Работа сотрудников',
    painId: 'staff-attendance',
    headline: 'Спрашиваете в WhatsApp, кто пришёл на работу?',
    subheadline:
      'Каждое утро уточняете у воспитателей — кто на месте, кто опаздывает, кто заболел. Потом вручную считаете отработанные часы и зарплату. Учёт отпусков и больничных — в голове или в тетради.',
    whatYouSee: {
      image:
        'https://cdn.magicpatterns.com/uploads/vSkEaYn4Nd9aDhoU3CG7d7/Screenshot_2026-05-19_at_17.17.15.png',
      description: 'Геочекин сотрудников + автоматический расчёт зарплаты',
      features: [
        'Сотрудник чекинится только если физически находится в садике',
        'Видно, кто пришёл вовремя, кто опоздал, кого отметили вручную',
        'Баланс отпусков и больничных по каждому сотруднику',
        'Расчёт зарплаты к выплате — автоматически, на основе отработанных часов',
      ],
    },
    steps: [
      {
        title: 'Сотрудник чекинится в приложении',
        description:
          'При входе в садик сотрудник нажимает «пришёл» — система проверяет геолокацию. Если он не на территории садика, чекин не проходит.',
        images: [
          'https://cdn.magicpatterns.com/uploads/n32yaS53YXYRr5Kmjs6Rr1/WhatsApp_Image_2026-05-14_at_13.44.38_(1).jpg',
          'https://cdn.magicpatterns.com/uploads/a5EZJzGtsJ6vbtKTG2ZGHf/WhatsApp_Image_2026-05-14_at_11.48.42.jpg',
        ],
        isMobile: true,
      },
      {
        title: 'Вы видите всю смену в реальном времени',
        description:
          'Кто пришёл, во сколько, кто опаздывает. Никаких опросов в WhatsApp.',
        image:
          'https://cdn.magicpatterns.com/uploads/vSkEaYn4Nd9aDhoU3CG7d7/Screenshot_2026-05-19_at_17.17.15.png',
      },
      {
        title: 'Система считает зарплату сама',
        description:
          'Отработанные часы, отпуска, больничные — всё учитывается автоматически. В конце месяца у вас готовая сумма к выплате по каждому сотруднику.',
        image:
          'https://cdn.magicpatterns.com/uploads/osJ1Lgbkk1LJt64m1LL99X/Screenshot_2026-05-19_at_17.17.59.png',
      },
    ],
    outcomes: [
      'Если воспитатель опаздывает 3 раза за неделю — вы видите это в данных, а не узнаёте от родителей',
      'Если сотрудник «был на работе», но геочекин не прошёл — повод разобраться, где он был',
      'Если в конце месяца кто-то спорит про часы — у вас точные данные, а не его слово против вашего',
      'Зарплату считаете один раз настроенной формулой, а не пересчитываете вручную каждый месяц',
    ],
  },
  'staff-tasks': {
    categoryId: 'staff',
    categoryIcon: '👥',
    categoryTitle: 'Работа сотрудников',
    painId: 'staff-tasks',
    headline: 'Задачи теряются в чатах сотрудников?',
    subheadline:
      'Поручили в WhatsApp, сотрудник прочитал, обещал сделать — и тишина. Через неделю не помните, что просили и кого. Сделано или нет? Кто отвечает? Где документы по задаче? Всё разбросано по чатам и личным сообщениям.',
    whatYouSee: {
      image:
        'https://cdn.magicpatterns.com/uploads/i2juvth1V32HLeFp51PJsU/Screenshot_2026-05-19_at_17.26.38.png',
      description:
        'Все задачи сотрудников — в одной системе с дедлайнами и статусами',
      features: [
        'Задачи с исполнителем, дедлайном, приоритетом и описанием',
        'Прикреплённые файлы и материалы — прямо в задаче',
        'Статусы: назначена / начата / выполнена',
        'Список задач по каждому сотруднику',
        'Комментарии и история по каждой задаче',
      ],
    },
    steps: [
      {
        title: 'Создаёте задачу с дедлайном',
        description:
          'Указываете исполнителя, описание, дедлайн, приоритет. Прикрепляете нужные документы или фото. Никаких «напиши потом в личку».',
        image:
          'https://cdn.magicpatterns.com/uploads/2kSYkvNfEdTMtFqKjdXaKG/Screenshot_2026-05-19_at_17.26.59.png',
      },
      {
        title: 'Сотрудник видит задачи в своём списке',
        description:
          'У каждого сотрудника — свой список задач. Он меняет статус по мере работы: начата → выполнена.',
        image:
          'https://cdn.magicpatterns.com/uploads/rwFv2cUnQEGMuaf5WRQGQ7/Screenshot_2026-05-19_at_17.28.28.png',
      },
      {
        title: 'Вы видите, что сделано, а что нет',
        description:
          'Открываете «Задачи» → видите список по каждому сотруднику. Сразу понятно, где сроки горят, где задача забыта, где всё в работе.',
        image:
          'https://cdn.magicpatterns.com/uploads/i2juvth1V32HLeFp51PJsU/Screenshot_2026-05-19_at_17.26.38.png',
      },
    ],
    outcomes: [
      'Если у Сымбат 5 задач «назначена» без движения неделю — повод поговорить, а не догадываться',
      'Если задача со сроком «31.05» висит до сих пор — система покажет это сама, не нужно держать в голове',
      'Если сотрудник говорит «вы мне это не поручали» — есть запись с датой и текстом',
      'Перестаёте быть единственным человеком, который помнит все задачи садика',
    ],
  },
  'docs-nurse-journals': {
    categoryId: 'documents',
    categoryIcon: '📁',
    categoryTitle: 'Документы и подготовка к проверкам',
    painId: 'docs-nurse-journals',
    headline: 'Не знаете, ведёте ли все обязательные журналы?',
    subheadline:
      'С 2027 года в РК вводятся внеплановые проверки детских садов. По данным за 2024 год, размер штрафов для садиков составил 5,2 млрд тенге. Журналы — первое, что проверяют. А знает о них обычно только медсестра — и если её нет, разобраться быстро не получится.',
    whatYouSee: {
      image:
        'https://cdn.magicpatterns.com/uploads/sZDTYzcKnezcxKg48Na2qY/Screenshot_2026-05-19_at_17.58.54.png',
      description:
        'Все обязательные журналы по СанПин РК — с готовыми шаблонами и автоматическим экспортом',
      features: [
        'Полный список обязательных журналов: медицинские, пищевые, санитарно-бытовые',
        'Для каждого журнала — периодичность по нормативу (ежедневно / 2 раза в день / раз в 10 дней / по событию)',
        'Видно, какие журналы ведутся регулярно, а где пропуски',
        'Заполнение — простыми кликами, без копирования таблиц',
        'Экспорт в Word — готовый документ по официальному шаблону с печатью организации',
      ],
    },
    steps: [
      {
        title: 'Открываете нужный журнал',
        description:
          'В системе уже есть все обязательные журналы по нормативам РК. Не нужно искать шаблоны или скачивать формы.',
        image:
          'https://cdn.magicpatterns.com/uploads/sZDTYzcKnezcxKg48Na2qY/Screenshot_2026-05-19_at_17.58.54.png',
      },
      {
        title: 'Сотрудник добавляет запись в пару кликов',
        description:
          'Вместо ручного заполнения таблицы в Word — выбирает сотрудника, отмечает осмотр, ставит подпись. Запись добавляется за 30 секунд. Журнал ведётся на русском и казахском.',
        image:
          'https://cdn.magicpatterns.com/uploads/kB8GPxinzwhdWEwxh7Yu4w/Screenshot_2026-05-19_at_17.51.25.png',
      },
      {
        title: 'Готовый документ в Word с печатью — в один клик',
        description:
          'Когда нужно распечатать журнал для проверки — нажимаете «Экспорт в Word». Получаете официальный документ по нужному шаблону с печатью вашей организации. На русском или казахском — на выбор. Никакого ручного оформления.',
        images: [
          'https://cdn.magicpatterns.com/uploads/5uiGZ2fTeVKa7shXXooagp/Screenshot_2026-05-19_at_17.51.52.png',
          'https://cdn.magicpatterns.com/uploads/dKEJSiTTgZUbh8xMANnpFT/Screenshot_2026-05-19_at_17.52.11.png',
        ],
      },
    ],
    outcomes: [
      'Если медсестра в отпуске — открываете журналы сами и видите всю картину',
      'Если в журнале температуры холодильников пропуски за неделю — повод поговорить до того, как придёт проверка',
      'Если завтра комиссия — экспортируете нужные журналы в Word за минуту, а не собираете бумаги по кабинетам',
      'Если медсестра увольняется — все журналы остаются в системе, новый сотрудник продолжает с того же места',
    ],
  },
  'docs-deadlines': {
    categoryId: 'documents',
    categoryIcon: '📁',
    categoryTitle: 'Документы и подготовка к проверкам',
    painId: 'docs-deadlines',
    headline: 'Не отслеживаете сроки документов?',
    subheadline:
      'Медсправки детей, документы сотрудников, лицензии, сертификаты — у всего свои сроки. Что-то уже могло истечь месяц назад, но вы об этом не знаете. На проверке выяснится — и это штраф.',
    whatYouSee: {
      image:
        'https://cdn.magicpatterns.com/uploads/i2i37DXfRoAMVtKqa8eJsv/Screenshot_2026-05-19_at_18.05.59.png',
      description:
        'Все документы садика в одном месте — с автоматическим контролем сроков',
      features: [
        'Хранилище документов организовано по стандартам аттестации',
        'Папки и подпапки по каждой категории — Контингент воспитанников, Учебно-методическая работа, Анализ кадрового потенциала и др.',
        'Загрузка любых документов: PDF, Word, CSV, изображения',
        'Доступ с любого устройства',
        'Автоматические метки: «Истекающие» и «Просроченные»',
        'Поиск по всем документам',
      ],
    },
    steps: [
      {
        title: 'Структура по стандартам аттестации — уже готова',
        description:
          'В системе встроены категории документов по требованиям аттестации детских садов. Не нужно придумывать структуру самостоятельно — просто загружаете документы в нужные разделы.',
        image:
          'https://cdn.magicpatterns.com/uploads/i2i37DXfRoAMVtKqa8eJsv/Screenshot_2026-05-19_at_18.05.59.png',
      },
      {
        title: 'Указываете срок документа при загрузке',
        description:
          'Загружаете медсправку или сертификат — указываете дату истечения. Документы без срока (например, методические) остаются с пометкой «Не указан».',
        image:
          'https://cdn.magicpatterns.com/uploads/6uiZbAez9CPafpz5dT5MK4/Screenshot_2026-05-19_at_18.12.14.png',
      },
      {
        title: 'Система подсвечивает проблемные документы',
        description:
          'Открываете «Документы» → видите фильтры «Истекающие» и «Просроченные». Сразу понятно, что нужно обновить и у каких сотрудников.',
        image:
          'https://cdn.magicpatterns.com/uploads/rFVjifcKbWMiNtEH2fHsBN/Screenshot_2026-05-19_at_18.06.13.png',
      },
    ],
    outcomes: [
      'Если у одного из детей медсправка просрочена 2 месяца назад — вы видите это сейчас, а не на проверке',
      'Если у воспитателя истекает сертификат через 2 недели — есть время подготовить новый, а не объясняться с комиссией',
      'Если приходит проверка — открываете нужную папку, всё на месте, по стандартам аттестации',
      'Если меняется ответственный за документы — структура остаётся, новый сотрудник продолжает с того же места',
    ],
  },
  'docs-storage': {
    categoryId: 'documents',
    categoryIcon: '📁',
    categoryTitle: 'Документы и подготовка к проверкам',
    painId: 'docs-storage',
    headline: 'Документы садика разбросаны по разным местам?',
    subheadline:
      'Часть в шкафу у методиста, часть на компьютере бухгалтера, часть в личных папках сотрудников, часть в WhatsApp. Когда нужен конкретный документ — звоните, ищете, ждёте. К проверке собираете всё в авральном режиме.',
    whatYouSee: {
      video:
        'https://cdn.magicpatterns.com/uploads/mgpcLWhbkKwrt2Sym53VKN/IMG_5676.MP4',
      isMobile: true,
      description: 'Единое хранилище документов с доступом с любого устройства',
      features: [
        'Папки уже организованы по структуре аттестации детских садов',
        'Можно создавать свои папки и подпапки под нужды садика',
        'Загрузка документов любых форматов: PDF, Word, CSV, изображения',
        'Доступ с компьютера, телефона, планшета — откуда угодно',
        'Подсветка документов с истекающим или истёкшим сроком годности',
        'Поиск по всем документам',
      ],
    },
    howItWorksVideo:
      'https://cdn.magicpatterns.com/uploads/9xfWiyFP8qpxmoYAt54EyJ/Screen Recording 2026-05-20 at 13.26.08.mov',
    steps: [
      {
        title: 'Структура аттестации — уже готова',
        description:
          'Открываете «Документы и шаблоны» → видите готовые категории по требованиям аттестации: Общая характеристика, Анализ кадрового потенциала, Контингент воспитанников, Учебно-методическая работа и т.д. Не нужно придумывать структуру самостоятельно.',
      },
      {
        title: 'Загружаете документы в нужные папки',
        description:
          'Загружаете медсправки, договоры, методические материалы. Можете создавать свои подпапки под группы, сотрудников или проекты. Указываете дату истечения для документов со сроком.',
      },
      {
        title: 'Доступ с любого устройства',
        description:
          'Сидите в кафе, на родительском собрании, у врача — открываете Meelo с телефона и находите нужный документ за секунды. Никаких «приеду в офис и пришлю».',
      },
    ],
    outcomes: [
      'Если медсестра в отпуске, а нужна справка ребёнка — открываете с телефона за 30 секунд',
      'Если у документа истекает срок — видите это заранее, а не на проверке',
      'Если приходит комиссия — структура готова по стандартам аттестации, не нужно собирать папки',
      'Если сотрудник увольняется — документы остаются в системе, не уходят с его компьютера',
    ],
  },
  'docs-methodology': {
    categoryId: 'documents',
    categoryIcon: '📁',
    categoryTitle: 'Документы и подготовка к проверкам',
    painId: 'docs-methodology',
    headline: 'Не уверены, что у вас есть все методические документы?',
    subheadline:
      'Типовой учебный план, перспективные планы, циклограммы — всё это требуется по Приказу 130. Составлять с нуля или дорабатывать под проверку — это недели работы методиста. И всегда есть страх, что чего-то не хватает или формат неправильный.',
    whatYouSee: {
      image:
        'https://cdn.magicpatterns.com/uploads/9yV9bqiPqBPseYsR3T2PVB/методическии_кабинет.png',
      description:
        'Все обязательные методические документы — создаются в пару кликов по утверждённым шаблонам',
      features: [
        'Типовой учебный план',
        'Перспективные планы',
        'Циклограммы',
        'Шаблоны соответствуют Приказу 130',
        'Документы Meelo уже проходили аттестацию в реальных садиках',
        'Экспорт в Word для печати и предъявления на проверке',
      ],
    },
    steps: [
      {
        title: 'Шаг 1. Выбираете нужный документ',
        description:
          'Открываете раздел методических документов → видите все обязательные по Приказу 130: учебный план, перспективные планы, циклограммы.',
        video:
          'https://cdn.magicpatterns.com/uploads/eQxd7dEbbAfdYJhXdFzEQm/ТУП.mov',
      },
      {
        title: 'Шаг 2. Создаёте документ в пару кликов',
        description:
          'Не составляете с нуля. Система использует утверждённые шаблоны — вы только заполняете данные вашего садика.',
        image:
          'https://cdn.magicpatterns.com/uploads/95twx6LKkjLh3LgZ5S7etb/циклограмма.png',
      },
      {
        title: 'Шаг 3. Скачиваете готовый документ',
        description:
          'Экспорт в Word по правильному формату — можно сразу печатать и подшивать.',
        image:
          'https://cdn.magicpatterns.com/uploads/mK198d5recqSkpzJt7cQqV/перспективныи_план.png',
      },
    ],
    outcomes: [
      'Если проверка спрашивает циклограмму — открываете и распечатываете за минуту',
      'Если методист увольняется — все документы остаются в системе, новый сотрудник работает по тем же шаблонам',
      'Если выходит обновление приказа — обновляются шаблоны в системе, а не вы переделываете каждый документ вручную',
      'Перестаёте бояться проверки методической части — документы соответствуют требованиям',
    ],
  },
  'finance-debtors': {
    categoryId: 'finance',
    categoryIcon: '💰',
    categoryTitle: 'Финансы и оплаты родителей',
    painId: 'finance-debtors',
    headline: 'Не вижу, кто из родителей ещё должен',
    subheadline:
      'раздражает процесс сбора оплаты, выяснять кто еще не оплатил и писать им в мессенджеры.',
    whatYouSee: {
      video:
        'https://cdn.magicpatterns.com/uploads/kvyqFSvwwFwdzaEECgVomj/Screen Recording 2026-05-18 at 15.35.33.mov',
      description:
        'получите список неоплативших в два клика и автоматические напоминания',
    },
    steps: [
      {
        title: 'Список всех недоплативших',
        description: 'Вся информация о долгах собрана в одном месте.',
        image:
          'https://cdn.magicpatterns.com/uploads/kpuLUABDNWRGbxeDGxzM7A/Screenshot_2026-05-18_at_15.33.25.png',
      },
      {
        title: 'Автоматическое напоминание',
        description:
          'Система сама формирует текст с суммой и ссылкой на оплату.',
        image:
          'https://cdn.magicpatterns.com/uploads/raejJWGrVA6w1cgKiTLQRN/ChatGPT_Image_19_мая_2026_г.,_12_20_04_(1).png',
      },
      {
        title: 'Родитель получает напоминание и видит свою историю оплат',
        description: 'Вся история платежей доступна родителю в приложении.',
        image:
          'https://cdn.magicpatterns.com/uploads/5ZTycTGkNF1kcCrGaPLRWx/ChatGPT_Image_19_мая_2026_г.,_12_20_05_(2).png',
      },
    ],
    outcomes: [
      'информация всегда под рукой',
      'напоминание занимает пару секунд',
      'текст автоматический',
    ],
  },
  'finance-overpay': {
    categoryId: 'finance',
    categoryIcon: '💰',
    categoryTitle: 'Финансы и оплаты родителей',
    painId: 'finance-overpay',
    headline: 'Каждый месяц вы переплачиваете — но не видите сколько',
    subheadline: '',
    whatYouSee: {
      video:
        'https://cdn.magicpatterns.com/uploads/aN4AH1cXc4FU43r2V9xfX3/Screen Recording 2026-05-19 at 12.50.44.mov',
      description:
        'Автоматический расчет закупа продуктов по вашей калькуляции с учетом остатков на складе',
    },
    steps: [
      {
        title: 'Меню и калькуляция',
        description:
          'В системе будет меню с каждой позицией по вашей калькуляции, с возможностью редактирования.',
        image:
          'https://cdn.magicpatterns.com/uploads/dyjJhDYdhGftUD6eYncJJ1/Screenshot_2026-05-19_at_12.52.49.png',
      },
    ],
    outcomes: [
      'Закупаете ровно столько, сколько нужно по меню',
      'Видите расход на питание по каждому ребёнку',
      'Остатки на складе под контролем — без списаний',
      'Понимаете реальную себестоимость дня в садике',
    ],
  },
  'finance-slow-payments': {
    categoryId: 'finance',
    categoryIcon: '💰',
    categoryTitle: 'Финансы и оплаты родителей',
    painId: 'finance-slow-payments',
    headline: 'Оплата от родителей собирается медленно',
    subheadline:
      'Каждый месяц собрать полную оплату — это квест с препятствиями',
    whatYouSee: {
      image:
        'https://cdn.magicpatterns.com/uploads/kpuLUABDNWRGbxeDGxzM7A/Screenshot_2026-05-18_at_15.33.25.png',
      description:
        'В дэшборде руководителя вы увидите суммы по оплатам и задолженностям',
    },
    howItWorksVideo:
      'https://cdn.magicpatterns.com/uploads/kvyqFSvwwFwdzaEECgVomj/Screen Recording 2026-05-18 at 15.35.33.mov',
    steps: [
      {
        title: 'Администратор отмечает оплату по каждому ребёнку',
        description:
          'Оплата фиксируется в системе с привязкой к ребёнку. Никаких таблиц.',
        image:
          'https://cdn.magicpatterns.com/uploads/7Tan4tCoZC26N1PHqU6F5U/панель_для_руководителя.png',
      },
      {
        title: 'Вы видите по группам должников и все детали для сверки',
        description:
          'Список должников по группам со всеми деталями: больничные, скидки, отпуска.',
        image:
          'https://cdn.magicpatterns.com/uploads/7Tan4tCoZC26N1PHqU6F5U/панель_для_руководителя.png',
      },
      {
        title: 'Администратор одним кликом напоминает должникам об оплате',
        description:
          'Напоминание уходит родителю в один клик. Не нужно писать вручную.',
        image:
          'https://cdn.magicpatterns.com/uploads/7Tan4tCoZC26N1PHqU6F5U/панель_для_руководителя.png',
      },
    ],
    outcomes: [
      'Ускоряет сбор оплаты',
      'Уменьшает количество ошибок в расчётах',
      'Вся необходимая информация в одной системе: даты отпуска, больничных, скидки и способ оплаты',
    ],
  },
  'nutrition-menu': {
    categoryId: 'nutrition',
    categoryIcon: '🍽',
    categoryTitle: 'Питание и закупки',
    painId: 'nutrition-menu',
    headline: 'Нет документации по перспективному меню',
    subheadline:
      'Перспективное меню, технологические карты, меню-раскладка на каждый день — всё это требуется по СанПин и спрашивают на проверках. Составить с нуля или поддерживать в актуальном виде — это часы работы для повара или медсестры каждый месяц.',
    whatYouSee: {
      video:
        'https://cdn.magicpatterns.com/uploads/2LMDq65k5EWSovKLQqwft5/перспективное меню 1.mov',
      description:
        'Готовый каталог блюд по СанПин — или ваше меню с автоматической документацией',
      features: [
        'Каталог блюд для дошкольных организаций, разработанный по нормам СанПин РК',
        'Перспективное меню на 20 дней',
        'Технологические карты на каждое блюдо',
        'Меню-раскладка на каждый день',
        'Возможность добавить свои блюда и составить своё меню',
        'Экспорт всех документов в Word для печати и проверок',
      ],
    },
    howItWorksVideo:
      'https://cdn.magicpatterns.com/uploads/rTW27QnxgRrDGLnwDjKbGU/меню-раскладка.mov',
    steps: [
      {
        title: 'Создаёте меню из готового каталога блюд',
        description:
          'Каталог блюд для дошкольных организаций, разработанный по нормам СанПин РК, уже в системе. Выбираете блюда — получаете перспективное меню на 20 дней.',
        image:
          'https://cdn.magicpatterns.com/uploads/k3sGE8Lr2SmU35bsEoHsDc/_технологическая_карта.png',
      },
      {
        title: 'Или загружаете своё меню и добавляете свои блюда',
        description:
          'Если у вас уже есть проверенное меню — добавляете свои блюда в систему. Meelo автоматически генерирует технологические карты и меню-раскладки.',
      },
    ],
    outcomes: [
      'Если приходит проверка и спрашивает технологические карты — есть готовые документы, не нужно срочно их составлять',
      'Если повар увольняется — меню и карты остаются в системе, новый сотрудник работает по той же документации',
      'Если хочется поменять блюдо — обновляете в одном месте, все документы пересчитываются',
      'Перестаёте тратить часы каждый месяц на ручное составление документов по питанию',
    ],
  },
  'nutrition-tech-cards': {
    categoryId: 'nutrition',
    categoryIcon: '🍽',
    categoryTitle: 'Питание и закупки',
    painId: 'nutrition-tech-cards',
    headline:
      'Составить техкарту на каждое блюдо — это часы работы. Поэтому обычно их нет — а на проверках спрашивают.',
    whatYouSee: {
      video:
        'https://cdn.magicpatterns.com/uploads/65XrLrN8mXAHuaTBShSCYV/Screen Recording 2026-05-20 at 14.37.06.mov',
      description:
        'Готовые технологические карты на блюда из каталога — или конструктор для своих блюд',
    },
    howItWorksVideo:
      'https://cdn.magicpatterns.com/uploads/waRyZVsDdNdB5cofyCHe4h/Screen Recording 2026-05-20 at 14.31.40.mov',
    steps: [
      {
        title: 'Используете готовые техкарты из каталога',
        description:
          'Все блюда из каталога СанПин уже имеют готовые технологические карты. Никакого ручного составления — открываете блюдо, видите готовый документ.',
      },
      {
        title: 'Или создаёте техкарту на своё блюдо',
        description:
          'Добавляете своё блюдо, заполняете поля (ингредиенты, нормы, технология) — система сама собирает техкарту в нужном формате.',
      },
    ],
    outcomes: [
      'Если проверка просит техкарту — открываете и распечатываете за минуту, не ищете по компьютерам и шкафам',
      'Если меняется повар — техкарты остаются в системе, новый сотрудник готовит по тем же стандартам',
      'Если в меню появляется новое блюдо — техкарта создаётся за 10 минут в конструкторе, а не за час вручную',
      'Перестаёте поддерживать техкарты в Word или на бумаге',
    ],
  },
  'nutrition-calc': {
    categoryId: 'nutrition',
    categoryIcon: '🍽',
    categoryTitle: 'Питание и закупки',
    painId: 'nutrition-calc',
    headline:
      'Без калькуляции невозможно понять, сколько реально нужно закупить продуктов. Закупаете на глаз — переплачиваете или остаётесь без необходимого. Складские остатки никто не считает.',
    whatYouSee: {
      video:
        'https://cdn.magicpatterns.com/uploads/jDjPGx1mwBDmnp467mXFba/расчет закупа.mov',
      description:
        'Калькуляция по каждому блюду — основа для автоматического расчёта закупок и списаний',
      features: [
        'Калькуляция автоматически рассчитывается из техкарт и количества детей',
        'Список продуктов к закупке на день, неделю, месяц',
        'Автоматическое списание продуктов со склада по факту приготовления',
        'Остатки на складе в реальном времени',
        'Подсветка продуктов, которые заканчиваются',
      ],
    },
    howItWorksVideo:
      'https://cdn.magicpatterns.com/uploads/7yEbPFCuJYpyjrPmAhUsR8/движение по меню.mov',
    steps: [
      {
        title: 'Система знает калькуляцию каждого блюда',
        description:
          'Из техкарт и количества детей Meelo автоматически считает, сколько продуктов нужно на день, неделю, месяц.',
      },
      {
        title: 'Получаете точный список закупок',
        description:
          'Не «на глаз» и не «как обычно». Точный расчёт: столько-то кг мяса, столько-то крупы, столько-то овощей.',
      },
      {
        title: 'Продукты списываются автоматически',
        description:
          'Когда блюдо приготовлено — продукты списываются со склада по калькуляции. Остатки всегда актуальны.',
      },
    ],
    outcomes: [
      'Если на этой неделе по меню 12 кг куриного филе — закупаете 12 кг, а не 15 «на всякий случай»',
      'Если на складе осталось 3 кг крупы при норме 8 — система покажет это до того, как повар придёт с вопросом',
      'Если в конце месяца проверяете расход — видите фактическое списание по калькуляции, а не оценочные цифры',
      'Перестаёте переплачивать за продукты и списывать испорченные остатки',
    ],
  },
  'nutrition-stock': {
    categoryId: 'nutrition',
    categoryIcon: '🍽',
    categoryTitle: 'Питание и закупки',
    painId: 'nutrition-stock',
    headline: 'Никак не ведете склад?',
    subheadline:
      'Не знаете, что закончилось, что нужно докупить, что лежит в избытке. Повар спрашивает — вы идёте проверять. Закупки делаются «как обычно», без понимания реального расхода.',
    whatYouSee: {
      image:
        'https://cdn.magicpatterns.com/uploads/u5jHaZAmdEws1fdqjUN7Ui/остатки.png',
      description:
        'Актуальные остатки на складе и автоматический список закупок',
      features: [
        'Остатки по каждому продукту в реальном времени',
        'Список того, что нужно докупить — на день, неделю, месяц',
        'Расход продуктов по факту приготовленных блюд',
        'Готовые отчёты по складу для бухгалтерии и проверок',
      ],
    },
    steps: [
      {
        title: 'Шаг 1. Вносите калькуляцию по каждому блюду в меню',
        description:
          'Один раз заносите состав и нормы для каждого блюда. Дальше система использует эти данные автоматически.',
      },
      {
        title: 'Шаг 2. Система считает расход и закупки',
        description:
          'По калькуляции и количеству детей Meelo сама рассчитывает: сколько продуктов нужно на меню, что закупать, что списывать со склада.',
        image:
          'https://cdn.magicpatterns.com/uploads/9xPQmrtqoGrHqoEGReyoNw/движения_по_складу.png',
      },
      {
        title: 'Шаг 3. Видите актуальную картину и готовые отчёты',
        description:
          'Открываете склад — видите остатки сейчас. Открываете отчёты — получаете готовые документы по расходу и закупкам.',
        image:
          'https://cdn.magicpatterns.com/uploads/iuhoXt7iWfhHjsW6YNmJjC/отчеты.png',
      },
    ],
    outcomes: [
      'Если на складе заканчивается мука — видите это до того, как повар придёт с вопросом',
      'Если закупка на неделю — открываете список и идёте в магазин, а не считаете в голове',
      'Если в конце месяца нужен отчёт — он уже готов, не нужно собирать данные вручную',
      'Перестаёте закупать "на всякий случай" и списывать продукты, которые не успели использовать',
    ],
  },
  'parents-birthdays': {
    categoryId: 'parents',
    categoryIcon: '💬',
    categoryTitle: 'Общение с родителями',
    painId: 'parents-birthdays',
    headline: 'Пропускаете дни рождения детей?',
    subheadline:
      'День рождения наступает — а вы узнаёте об этом утром от воспитателя или от родителей. Не успеваете подготовить поздравление, украсить группу, заказать подарок. Родители помнят, какие садики поздравляют детей, а какие нет.',
    whatYouSee: {
      video:
        'https://cdn.magicpatterns.com/uploads/2oZXQ2v2fepWRk8tD2s3o2/Screen Recording 2026-05-20 at 15.22.12.mov',
      description: 'Ближайшие дни рождения детей — заранее в одном месте',
      features: [
        'Календарь дней рождения по всем группам',
        'Список ближайших именинников на неделю и месяц вперёд',
        'Информация о ребёнке: возраст, группа, контакты родителей',
      ],
    },
    steps: [
      {
        title: 'Шаг 1. Даты рождения уже в системе',
        description:
          'При зачислении ребёнка дата рождения вносится в карточку. Никаких отдельных таблиц или списков.',
      },
      {
        title: 'Шаг 2. Видите ближайшие дни рождения',
        description:
          'Открываете календарь → видите, у кого день рождения на этой неделе, в следующей, в этом месяце.',
      },
    ],
    outcomes: [
      'Если у ребёнка день рождения через неделю — успеваете подготовить поздравление и украсить группу',
      'Если в одной группе 3 именинника в месяце — можете сделать общий праздник, а не три отдельных в спешке',
      'Если родители видят, что садик помнит о дне рождения — это сильный фактор удержания',
      'Перестаёте узнавать о праздниках в день праздника',
    ],
  },
  'finance-child-view': {
    categoryId: 'finance',
    categoryIcon: '💰',
    categoryTitle: 'Финансы и оплаты родителей',
    painId: 'finance-child-view',
    headline: 'Вся информация о ребенке в одном месте',
    subheadline:
      'история посещения, история оплат, документы, контакты родителя, информацию по здоровью в два клика',
    whatYouSee: {
      video:
        'https://cdn.magicpatterns.com/uploads/duFaB9Y9aRvS2EYoga4bTG/Screen Recording 2026-05-20 at 15.30.09.mov',
      description: 'Карточка ребёнка — вся информация в одном месте',
      features: [
        'Личные данные и контакты родителей',
        'История посещаемости за любой период',
        'История оплат: что заплачено, что должны, какие были скидки',
        'Документы ребёнка с подсветкой сроков',
        'Группа, воспитатель, история переводов',
      ],
    },
    howItWorksVideo:
      'https://cdn.magicpatterns.com/uploads/hmmxmQpFZFhQsMG8eNdNn2/Screen Recording 2026-05-20 at 15.30.09.mov',
    steps: [
      {
        title: 'Шаг 1. Открываете карточку ребёнка',
        description:
          'Из любого раздела системы — список детей, поиск, группа — попадаете в единую карточку.',
      },
      {
        title: 'Шаг 2. Видите всю историю в одном месте',
        description:
          'Посещаемость, оплаты, документы, медсправки — всё на одном экране. Не нужно открывать три системы и звонить трём сотрудникам.',
      },
    ],
    outcomes: [
      'Если родитель приходит с вопросом по оплате — открываете карточку и видите всю историю за секунды',
      'Если возникает спор о посещаемости — есть точные данные за любой период',
      'Если медсправка у ребёнка истекает — видите это сразу, без звонка медсестре',
      'Перестаёте быть единственным человеком, который собирает информацию по ребёнку из разных источников',
    ],
  },
}
